import * as types from '../mutation-types';
import axios from 'axios';
import config from '@/config';
import { loadData } from '@/utils/loading';
import { castOutVariable, prepareSceneVariables } from '@/utils/variables';


const state = {
  scenes: [],
  scenesVars: {},
};

const mutations = {
  [types.SET_SCENES]: (state, scenes) => {
    state.scenes = scenes;
  },
  [types.SET_SCENES_VARS]: (state, scenesVars) => {
    state.scenesVars = prepareSceneVariables(scenesVars);
  },
  [types.ADD_VARS_INTO_SCENE]: (state, sceneVars) => {
    state.scenesVars = { ...state.scenesVars, ...prepareSceneVariables(sceneVars) };
  },
  [types.ADD_SCENE]: (state, scene) => {
    state.scenes.push(scene);
  },
  [types.ADD_SERVICE_INTO_SCENE]: (state, { scene, serviceId }) => {
    scene.services.push(serviceId);
  },
  [types.REMOVE_SERVICE_FROM_SCENE]: (state, { scene, serviceId }) => {
    scene.services.splice(scene.services.findIndex(s => s.id === serviceId), 1);
  },
  [types.REMOVE_VARS_FROM_SCENE]: (state, sceneVarsIds) => {
    sceneVarsIds.forEach((varItemId) => {
      delete state.scenesVars[varItemId];
    });
  },
  [types.UPDATE_SCENE_VAR_VALUE]: (state, { varItem, value }) => {
    varItem.value = value; // eslint-disable-line no-param-reassign
  },
  [types.REMOVE_SCENE]: (state, scene) => {
    // first at all we have to empty services,
    // otherwise we get error on control panel
    scene.services = [];
    state.scenes.splice(state.scenes.findIndex(s => s.id === scene.id), 1);
  },
  [types.UPDATE_SCENE]: (state, { scene, payload }) => {
    scene.name = payload.name;
    scene.icon = payload.icon;
  },
  [types.REMOVE_SCENE_VARS]: (state, sceneId) => {
    state.scenesVars.filter(s => s.sceneId === sceneId).forEach((sceneVar) => {
      delete state.scenesVars[sceneVar.id];
    });
  },
  [types.SET_FAVORITE_SCENE]: (state, { scene, favorite }) => {
    scene.favorite = favorite;
  },
};

const getters = {
  getScenes: state => state.scenes,
  getScenesVars: state => Object.values(state.scenesVars),
  getFavoriteScenes: state => state.scenes.filter(scene => scene.favorite),
  getSceneById: state => id => state.scenes.filter(
    scene => scene.id === id,
  )[0],
  getActorVarsByScene: (state, getters) => (actor, scene) => {
    return getters.getScenesVars.filter(
      varItem => varItem.actorId === actor.id && varItem.sceneId === scene.id,
    );
  },
  getSceneVarsBySceneId: (state, getters) => sceneId => getters.getScenesVars.filter(
    sceneVar => sceneVar.sceneId === sceneId,
  ),
};

const actions = {
  async loadScenes({ commit, rootState }) {
    const url = new URL(
      config.ICOOL.API.USER_SCENES_PATH(rootState.snapshot.currentSnapshot.id),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return loadData(commit, types.SET_SCENES, url);
  },
  async loadScenesVars({ commit, rootState }) {
    const url = new URL(
      config.ICOOL.API.USER_SCENES_VARS_PATH(rootState.snapshot.currentSnapshot.id),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return loadData(commit, types.SET_SCENES_VARS, url);
  },
  addScene({ commit, rootState }, scene) {
    const url = new URL(
      config.ICOOL.API.USER_SCENES_PATH(rootState.snapshot.currentSnapshot.id),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return new Promise((resolve, reject) => {
      axios
        .put(url, scene)
        .then((response) => {
          commit(types.ADD_SCENE, response.data);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  addServiceIntoScene({ commit, getters }, { serviceId, sceneId }) {
    const url = new URL(
      config.ICOOL.API.USER_SCENE_SERVICE_PATH(sceneId, serviceId),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return new Promise((resolve, reject) => {
      axios
        .put(url, {})
        .then((response) => {
          const scene = getters.getSceneById(sceneId);
          commit(types.ADD_SERVICE_INTO_SCENE, { scene, serviceId });
          commit(types.ADD_VARS_INTO_SCENE, response.data);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  removeServiceFromScene({ commit, getters }, { serviceId, sceneId }) {
    const url = new URL(
      config.ICOOL.API.USER_SCENE_SERVICE_PATH(sceneId, serviceId),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return new Promise((resolve, reject) => {
      axios
        .delete(url)
        .then((response) => {
          const scene = getters.getSceneById(sceneId);
          commit(types.REMOVE_SERVICE_FROM_SCENE, { scene, serviceId });
          commit(types.REMOVE_VARS_FROM_SCENE, response.data);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  removeScene({ commit, rootState }, scene) {
    const url = new URL(
      config.ICOOL.API.USER_SCENE_PATH(rootState.snapshot.currentSnapshot.id, scene.id),
      config.ICOOL.BACKEND_HOST,
    ).href;
    return new Promise((resolve, reject) => {
      axios
        .delete(url)
        .then((response) => {
          commit(types.REMOVE_SCENE, scene);
          commit(types.REMOVE_SCENE_VARS, scene.id);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  updateScene({ commit, getters, rootState }, payload) {
    const url = new URL(
      config.ICOOL.API.USER_SCENE_PATH(rootState.snapshot.currentSnapshot.id, payload.sceneId),
      config.ICOOL.BACKEND_HOST,
    ).href;
    const data = {
      name: payload.name,
      icon: payload.icon,
    };
    return new Promise((resolve, reject) => {
      axios
        .patch(url, data)
        .then((response) => {
          const scene = getters.getSceneById(payload.sceneId);
          commit(types.UPDATE_SCENE, { scene, payload });
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  updateSceneVarValue({ commit }, payload) {
    const url = new URL(
      config.ICOOL.API.USER_SCENE_VAR_UPDATE_PATH(payload.varItem.id),
      config.ICOOL.BACKEND_HOST,
    ).href;
    const data = {
      value: String(castOutVariable(payload.value, payload.varItem.type)),
    };
    return new Promise((resolve, reject) => {
      axios
        .patch(url, data)
        .then((response) => {
          commit(types.UPDATE_SCENE_VAR_VALUE, payload);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject();
        })
        .finally(() => {
        });
    });
  },
  setFavoriteScene({ commit }, { scene, favorite }) {
    commit(types.SET_FAVORITE_SCENE, { scene, favorite });
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};
